import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useParams } from "react-router-dom";
import SignaturePadComponent from "../components/SignaturePadComponent";
import SignerFieldOverlay from "../components/signer/SignerFieldOverlay";
import OtpVerification from "../components/OtpVerification";
import ModalShell from "../components/common/ModalShell";
import { useToast } from "../components/common/ToastProvider";
import {
  SkeletonCard,
  SkeletonBlock,
} from "../components/common/SkeletonBlock";
import { createSignerEventSource } from "../api/realtimeApi";
import {
  getSignerPreview,
  signField,
  signAllFields,
  rejectSigning,
} from "../api/signerApi";

function formatDateTime(value) {
  if (!value) return "-";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

export default function SignDocumentPage() {
  const { token } = useParams();
  const toast = useToast();

  const [preview, setPreview] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [signature, setSignature] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [savedSignature, setSavedSignature] = useState(
    sessionStorage.getItem("savedSignature") || null,
  );
  const [applyToAll, setApplyToAll] = useState(false);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [submittingSign, setSubmittingSign] = useState(false);
  const [submittingReject, setSubmittingReject] = useState(false);
  const [liveConnected, setLiveConnected] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const lastPreviewRequestAtRef = useRef(0);

  const loadPreview = useCallback(
    async (showErrors = true, force = false) => {
      if (!token) return;

      const now = Date.now();
      const minRefreshMs = 30000;
      if (!force && now - lastPreviewRequestAtRef.current < minRefreshMs) {
        return;
      }

      lastPreviewRequestAtRef.current = now;

      try {
        const res = await getSignerPreview(token);
        setPreview(res.data);
        setLastRefreshedAt(new Date());
      } catch (e) {
        if (showErrors) {
          toast.error("Failed to load signing preview.");
        }
      }
    },
    [token, toast],
  );

  useEffect(() => {
    loadPreview(true, true);
  }, [token, loadPreview]);

  useEffect(() => {
    if (!token) return;
    const eventSource = createSignerEventSource(token);
    eventSource.addEventListener("connected", () => {
      setLiveConnected(true);
    });
    eventSource.addEventListener("signer-update", async () => {
      await loadPreview(false);
      // if (!submittingSign && !submittingReject) {
      //   toast.info("Signing page updated instantly from live activity.");
      // }
    });
    eventSource.onerror = () => {
      setLiveConnected(false);
    };
    return () => {
      eventSource.close();
      setLiveConnected(false);
    };
  }, [token, loadPreview]);

  useEffect(() => {
    setSignature(null);
  }, [selectedField]);

  useEffect(() => {
    if (preview && preview.completedFields === preview.totalFields) {
      sessionStorage.removeItem("savedSignature");
      setSavedSignature(null);
      setSelectedField(null);
      setSignature(null);
      setApplyToAll(false);
      setShowSignatureModal(false);
      setShowOtp(false);
      setShowRejectBox(false);
    }
  }, [preview]);

  const progressPercent = useMemo(() => {
    if (!preview || !preview.totalFields) return 0;
    return Math.round((preview.completedFields / preview.totalFields) * 100);
  }, [preview]);

  const handleFieldClick = (field) => {
    if (field.completed) return;

    if (preview.documentStatus === "REJECTED") {
      toast.error(
        "This document has been rejected and is no longer available for signing.",
      );
      return;
    }

    if (preview.documentStatus === "EXPIRED") {
      toast.error("This signing request has expired.");
      return;
    }

    if (!preview.isSignerTurn) {
      toast.warning(
        preview.lockMessage ||
          "Another signer must complete signing before you can sign.",
      );
      return;
    }

    setSelectedField(field);

    if (!preview.otpVerified) {
      setShowOtp(true);
      return;
    }

    if (!preview.canSignNow) {
      toast.warning(preview.lockMessage || "You are not allowed to sign yet.");
      return;
    }

    setShowSignatureModal(true);
  };

  const handleSignatureSave = (sig) => {
    setSignature(sig);
    setSavedSignature(sig);
    sessionStorage.setItem("savedSignature", sig);
    toast.success("Signature saved.");
  };

  const sign = async () => {
    if (!signature) return;

    try {
      setSubmittingSign(true);

      if (applyToAll) {
        await signAllFields(preview.documentId, {
          accessToken: token,
          signatureBase64: signature,
        });
      } else {
        if (!selectedField) return;

        await signField(preview.documentId, {
          accessToken: token,
          fieldId: selectedField.fieldId,
          signatureBase64: signature,
        });
      }

      setSignature(null);
      setSelectedField(null);
      setShowOtp(false);
      setShowSignatureModal(false);
      setApplyToAll(false);

      await loadPreview(true, true);
      toast.success(
        applyToAll
          ? "Signature applied to all remaining fields."
          : "Field signed successfully.",
      );
    } catch (e) {
      toast.error("Failed to sign document.");
    } finally {
      setSubmittingSign(false);
    }
  };

  const handleOtpVerified = async () => {
    setShowOtp(false);
    await loadPreview(true, true);
    toast.success("OTP verified successfully.");

    if (selectedField) {
      setShowSignatureModal(true);
    }
  };

  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.warning("Please provide rejection reason.");
      return;
    }

    try {
      setSubmittingReject(true);

      await rejectSigning(preview.documentId, {
        accessToken: token,
        reason: rejectReason,
      });

      setShowRejectBox(false);
      setRejectReason("");
      setSelectedField(null);
      setSignature(null);
      setApplyToAll(false);

      await loadPreview(true, true);
      toast.success("You have declined signing.");
    } catch (e) {
      toast.error("Failed to decline signing.");
    } finally {
      setSubmittingReject(false);
    }
  };

  const closeSignatureModal = () => {
    if (submittingSign) return;
    setShowSignatureModal(false);
    setSignature(null);
    setApplyToAll(false);
  };

  if (!preview) {
    return (
      <div className="signer-shell">
        <div className="signer-topbar">
          <div className="signer-topbar-inner">
            <div className="signer-brand">
              <h1 className="signer-brand-title">Secure Document Signing</h1>
              <p className="signer-brand-subtitle">
                Loading signing session...
              </p>
            </div>
          </div>
        </div>

        <div className="signer-content">
          <SkeletonCard lines={3} titleWidth="45%" />
          <div className="section-space sign-layout">
            <div className="card">
              <SkeletonBlock height={18} width="30%" />
              <div style={{ marginTop: 16 }}>
                <SkeletonBlock height={520} width="100%" radius={16} />
              </div>
            </div>
            <SkeletonCard lines={6} titleWidth="40%" />
          </div>
        </div>
      </div>
    );
  }

  const isCompleted = preview.completedFields === preview.totalFields;
  const isRejected =
    preview.signerStatus === "REJECTED" ||
    preview.documentStatus === "REJECTED";
  const isExpired = preview.documentStatus === "EXPIRED";
  const isLocked = isCompleted || isRejected || isExpired;
  const lockedBySequence = !preview.isSignerTurn;

  return (
    <div className="signer-shell">
      <div className="signer-topbar">
        <div className="signer-topbar-inner">
          <div className="signer-brand">
            <h1 className="signer-brand-title">Secure Document Signing</h1>
            <p className="signer-brand-subtitle">
              Review, verify, and sign your document with confidence.
            </p>
          </div>

          <div className="top-chip">Protected Workflow</div>
        </div>
      </div>

      <div className="signer-content">
        <div className="signer-hero">
          <div className="signer-summary-card">
            <div className="page-header" style={{ marginBottom: 0 }}>
              <div>
                <h2 className="page-title" style={{ fontSize: 26 }}>
                  {preview.documentName}
                </h2>
                <p className="page-subtitle">
                  Assigned to {preview.signerName} ({preview.signerEmail})
                </p>
              </div>
            </div>

            <div className="signer-status-row">
              <span
                className={`badge badge-${String(preview.documentStatus).toLowerCase()}`}
              >
                Document: {preview.documentStatus}
              </span>
              <span
                className={`badge badge-${String(preview.signerStatus).toLowerCase()}`}
              >
                Signer: {preview.signerStatus}
              </span>
              <span className="badge badge-uploaded">
                Progress: {preview.completedFields}/{preview.totalFields}
              </span>
            </div>

            <div className="signer-progress-block">
              <div className="signer-progress-header">
                <span>Signing Progress</span>
                <strong>{progressPercent}%</strong>
              </div>

              <div className="signer-progress-track">
                <div
                  className="signer-progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <div className="workspace-live-strip">
              <div className="workspace-live-pill">
                Live Status:{" "}
                <strong>{liveConnected ? "Connected" : "Disconnected"}</strong>
              </div>

              <div className="workspace-live-pill">
                Last Refresh:{" "}
                <strong>
                  {lastRefreshedAt
                    ? new Intl.DateTimeFormat("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        second: "2-digit",
                      }).format(lastRefreshedAt)
                    : "-"}
                </strong>
              </div>
            </div>
          </div>
        </div>

        <div className="sign-layout">
          <div>
            {isCompleted ? (
              <div className="signer-state-panel signer-state-success">
                <div className="signer-state-icon">✓</div>
                <div>
                  <div className="signer-state-title">
                    Document signing completed successfully
                  </div>
                  <div className="signer-state-text">
                    All required fields have been completed for your signing
                    step.
                  </div>
                </div>
              </div>
            ) : isRejected ? (
              <div className="signer-state-panel signer-state-error">
                <div className="signer-state-icon">!</div>
                <div>
                  <div className="signer-state-title">
                    This document has been rejected
                  </div>
                  <div className="signer-state-text">
                    This signing request is no longer available for completion.
                  </div>
                </div>
              </div>
            ) : isExpired ? (
              <div className="signer-state-panel signer-state-error">
                <div className="signer-state-icon">⏰</div>
                <div>
                  <div className="signer-state-title">
                    This signing request has expired
                  </div>
                  <div className="signer-state-text">
                    Expired on {formatDateTime(preview.expiresAt)}.
                  </div>
                </div>
              </div>
            ) : lockedBySequence ? (
              <div className="signer-state-panel signer-state-warning">
                <div className="signer-state-icon">⌛</div>
                <div>
                  <div className="signer-state-title">
                    Waiting for a previous signer
                  </div>
                  <div className="signer-state-text">
                    {preview.lockMessage ||
                      "Another signer must complete signing before you can sign."}
                  </div>
                </div>
              </div>
            ) : !preview.otpVerified ? (
              <div className="signer-state-panel signer-state-warning">
                <div className="signer-state-icon">🔐</div>
                <div>
                  <div className="signer-state-title">
                    OTP verification required
                  </div>
                  <div className="signer-state-text">
                    Review the document first. OTP verification will be required
                    when you begin signing.
                  </div>
                </div>
              </div>
            ) : (
              <div className="signer-state-panel signer-state-info">
                <div className="signer-state-icon">✍</div>
                <div>
                  <div className="signer-state-title">Ready to sign</div>
                  <div className="signer-state-text">
                    Select a signature field on the document to continue.
                  </div>
                </div>
              </div>
            )}

            <div className="signer-document-card">
              <SignerFieldOverlay
                preview={preview}
                onFieldClick={handleFieldClick}
              />
            </div>
          </div>

          <div className="sign-panel-sticky">
            <div className="signer-side-card">
              <h3 className="signer-panel-title">Signing Summary</h3>
              <p className="signer-panel-subtitle">
                Your current signing state and next required action.
              </p>

              <div className="status-kpi-grid">
                <div className="status-kpi">
                  <div className="status-kpi-label">Document Status</div>
                  <div className="status-kpi-value">
                    {preview.documentStatus}
                  </div>
                </div>
                <div className="status-kpi">
                  <div className="status-kpi-label">Signer Status</div>
                  <div className="status-kpi-value">{preview.signerStatus}</div>
                </div>
                <div className="status-kpi">
                  <div className="status-kpi-label">Fields Completed</div>
                  <div className="status-kpi-value">
                    {preview.completedFields} / {preview.totalFields}
                  </div>
                </div>
                <div className="status-kpi">
                  <div className="status-kpi-label">Turn Status</div>
                  <div className="status-kpi-value">
                    {preview.isSignerTurn ? "Your Turn" : "Waiting"}
                  </div>
                </div>
                <div className="status-kpi">
                  <div className="status-kpi-label">Expires At</div>
                  <div className="status-kpi-value">
                    {formatDateTime(preview.expiresAt)}
                  </div>
                </div>
                <div className="status-kpi">
                  <div className="status-kpi-label">OTP Verified</div>
                  <div className="status-kpi-value">
                    {preview.otpVerified ? "Yes" : "No"}
                  </div>
                </div>
              </div>
            </div>

            <div className="signer-side-card section-space">
              <h3 className="signer-panel-title">Live Controls</h3>
              <p className="signer-panel-subtitle">
                Refresh this page instantly from server-side activity.
              </p>

              <div className="inline-actions">
                <button
                  className="btn btn-light"
                  onClick={() => loadPreview(true, true)}
                >
                  Refresh Now
                </button>
              </div>
            </div>

            {!isLocked && preview.isSignerTurn && (
              <div className="signer-side-card section-space">
                <h3 className="signer-panel-title">Actions</h3>
                <p className="signer-panel-subtitle">
                  Continue signing or decline this request.
                </p>

                <div className="inline-actions">
                  {!preview.otpVerified && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedField(null);
                        setShowOtp(true);
                      }}
                    >
                      Verify OTP
                    </button>
                  )}

                  {preview.otpVerified && !isCompleted && (
                    <button
                      className="btn btn-light"
                      onClick={() => {
                        setSelectedField(
                          selectedField ||
                            (preview.fields || []).find((f) => !f.completed) ||
                            null,
                        );
                        setShowSignatureModal(true);
                      }}
                    >
                      Open Signature Pad
                    </button>
                  )}

                  <button
                    className="btn btn-danger"
                    onClick={() => setShowRejectBox(true)}
                  >
                    Decline to Sign
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showOtp && !preview.otpVerified && !isLocked && !lockedBySequence && (
        <ModalShell
          title="OTP Verification"
          subtitle="Verify your identity before signing the document."
          onClose={() => setShowOtp(false)}
          width={560}
        >
          <OtpVerification token={token} onVerified={handleOtpVerified} />
        </ModalShell>
      )}

      {showRejectBox && !isLocked && preview.isSignerTurn && (
        <ModalShell
          title="Decline Signing"
          subtitle="Please provide a reason for declining this signing request."
          onClose={() => setShowRejectBox(false)}
          width={620}
        >
          <textarea
            className="textarea"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for declining..."
          />

          <div className="inline-actions" style={{ marginTop: 14 }}>
            <button
              className="btn btn-danger"
              onClick={reject}
              disabled={submittingReject}
            >
              {submittingReject ? "Submitting..." : "Confirm Decline"}
            </button>
            <button
              className="btn btn-light"
              onClick={() => setShowRejectBox(false)}
              disabled={submittingReject}
            >
              Cancel
            </button>
          </div>
        </ModalShell>
      )}

      {showSignatureModal &&
        preview.otpVerified &&
        !isLocked &&
        !lockedBySequence && (
          <ModalShell
            title="Provide Signature"
            subtitle="Draw your signature and apply it to the selected field."
            onClose={closeSignatureModal}
            width={760}
          >
            <div className="signature-box">
              <SignaturePadComponent onSave={handleSignatureSave} />
            </div>

            <div className="inline-actions" style={{ marginTop: 14 }}>
              {savedSignature && (
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setSignature(savedSignature)}
                >
                  Use Saved Signature
                </button>
              )}
            </div>

            <div className="checkbox-row">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
              />
              <span>Apply this signature to all remaining fields</span>
            </div>

            <div className="modal-footer-actions">
              <button
                className="btn btn-light"
                onClick={closeSignatureModal}
                disabled={submittingSign}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                disabled={!signature || submittingSign}
                onClick={sign}
              >
                {submittingSign
                  ? "Submitting..."
                  : applyToAll
                    ? "Confirm Sign All"
                    : "Confirm Sign"}
              </button>
            </div>
          </ModalShell>
        )}
    </div>
  );
}
